import { useEffect, useState } from 'react'
import {
  getSpareColours,
  getSpareItems,
  getSpareProdFam,
  getSpareProdFilter,
  type SpareColour,
  type SpareItem,
  type SpareProdFamRow,
  type SpareProdFilterRow,
} from '@alwindows/shared/api/spareParts'
import { groupByRefMaterial, sortVariantsByColour, type Product } from './types'

export type CatalogData = {
  products: Product[]
  prodFams: SpareProdFamRow[]
  prodFilters: SpareProdFilterRow[]
  colours: SpareColour[]
  loading: boolean
  error: string | null
}

/**
 * One-shot load of the four catalog endpoints. Items are grouped by
 * RefMaterial (or MaterialNo when RefMaterial is empty) into products, and
 * within each product the variants are sorted by the colour-map Sortorder.
 */
export function useCatalog(): CatalogData {
  const [products, setProducts] = useState<Product[]>([])
  const [prodFams, setProdFams] = useState<SpareProdFamRow[]>([])
  const [prodFilters, setProdFilters] = useState<SpareProdFilterRow[]>([])
  const [colours, setColours] = useState<SpareColour[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [items, fams, filters, cols] = await Promise.all([
          getSpareItems(),
          getSpareProdFam(),
          getSpareProdFilter(),
          getSpareColours(),
        ])
        if (cancelled) return

        const colourOrder = new Map<string, number>()
        for (const c of cols) colourOrder.set(c.ColourCode, c.Sortorder)

        const grouped = groupByRefMaterial(items as SpareItem[]).map((p) => ({
          ...p,
          variants: sortVariantsByColour(p.variants, colourOrder),
        }))

        setProducts(grouped)
        setProdFams(fams)
        setProdFilters(filters)
        setColours(cols)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load catalog.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return { products, prodFams, prodFilters, colours, loading, error }
}
